import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Globe, MousePointer, MapPin, GraduationCap, FlaskConical, Users, BookOpen, Plane, ChevronRight } from 'lucide-react';

const INTL_IMG   = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda08.jpg';
const PROSP_IMG1 = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda13.jpg';
const PROSP_IMG2 = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda14.jpg';

/* ── Çapraz İkili Banner ─────────────────────────────────────────────────── */
const DiagonalDualBanner = ({ isEn, prefix }) => {
  const departments = isEn ? [
    { name: 'Actuarial Science', href: 'https://aktuerya.hacettepe.edu.tr/' },
    { name: 'Biology',           href: 'https://biology.hacettepe.edu.tr/' },
    { name: 'Chemistry',         href: 'https://chem.hacettepe.edu.tr/'    },
    { name: 'Mathematics',       href: 'https://mat.hacettepe.edu.tr/'     },
    { name: 'Statistics',        href: 'https://stat.hacettepe.edu.tr/'    },
  ] : [
    { name: 'Aktüerya Bilimleri', href: 'https://aktuerya.hacettepe.edu.tr/' },
    { name: 'Biyoloji',           href: 'https://biology.hacettepe.edu.tr/' },
    { name: 'Kimya',              href: 'https://chem.hacettepe.edu.tr/'    },
    { name: 'Matematik',          href: 'https://mat.hacettepe.edu.tr/'     },
    { name: 'İstatistik',         href: 'https://stat.hacettepe.edu.tr/'    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl mb-14 md:min-h-[640px]"
      style={{ boxShadow: '0 28px 72px rgba(0,0,0,0.22)' }}
    >

      {/* ══════════ SOL PANEL — Aday Öğrenciler ══════════ */}
      {/* Masaüstü: sol taraf, clip-path ile çapraz kesilmiş */}
      <div
        className="absolute inset-0"
        style={{ clipPath: 'polygon(0 0, 63% 0, 45% 100%, 0 100%)' }}
      >
        <img src={PROSP_IMG1} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(140deg, rgba(55,4,13,0.96) 0%, rgba(95,8,20,0.85) 45%, rgba(12,3,7,0.5) 100%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to top, rgba(10,1,4,0.97) 0%, rgba(10,1,4,0.2) 45%, transparent 100%)'
        }} />
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(190,35,65,0.25) 0%, transparent 65%)'
        }} />
      </div>

      {/* ══════════ SAĞ PANEL — Uluslararası Öğrenciler ══════════ */}
      <div
        className="absolute inset-0"
        style={{ clipPath: 'polygon(63% 0, 100% 0, 100% 100%, 45% 100%)' }}
      >
        <img src={INTL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(140deg, rgba(6,10,55,0.92) 0%, rgba(12,35,120,0.82) 45%, rgba(0,80,130,0.48) 100%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to top, rgba(3,6,38,0.97) 0%, rgba(3,6,38,0.15) 50%, transparent 100%)'
        }} />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(56,189,248,0.2) 0%, transparent 65%)'
        }} />
      </div>

      {/* ══════════ ÇAPRAZ IŞIK ÇİZGİSİ ══════════ */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" preserveAspectRatio="none" viewBox="0 0 100 100">
        {/* Yumuşak parlama */}
        <line x1="63" y1="0" x2="45" y2="100" stroke="rgba(255,255,255,0.07)" strokeWidth="3.5" />
        {/* Keskin ince çizgi */}
        <line x1="63" y1="0" x2="45" y2="100" stroke="rgba(255,255,255,0.22)" strokeWidth="0.4" />
      </svg>

      {/* Animasyonlu ışık huzmesi çapraz çizgide */}
      <motion.div
        className="absolute pointer-events-none z-10"
        style={{
          width: '2px',
          top: 0,
          bottom: 0,
          left: '54%',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(200,220,255,0.18) 40%, rgba(200,220,255,0.18) 60%, transparent 100%)',
          transform: 'skewX(-10deg)',
          filter: 'blur(6px)',
        }}
        animate={{ opacity: [0, 0.6, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />

      {/* ══════════ MASAÜSTÜ İÇERİK KATMANI ══════════ */}
      <div className="hidden md:block absolute inset-0 z-30" style={{ minHeight: '640px' }}>

        {/* Üst başlık — SOL: Aday Öğrenciler */}
        <motion.div
          className="absolute top-8 left-9"
          initial={{ opacity: 0, y: -14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl" style={{
            background: 'rgba(123,13,30,0.55)',
            border: '1px solid rgba(210,60,80,0.35)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 6px 24px rgba(80,8,18,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}>
            <div className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0" style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <GraduationCap className="w-3.5 h-3.5" style={{ color: '#fca5a5' }} />
            </div>
            <span className="font-black uppercase whitespace-nowrap" style={{ fontSize: '0.8rem', letterSpacing: '0.07em', color: '#fff' }}>
              {isEn ? 'Prospective Students' : 'Aday Öğrenciler'}
            </span>
          </div>
        </motion.div>

        {/* Üst başlık — SAĞ: Uluslararası Öğrenciler */}
        <motion.div
          className="absolute top-8 right-10"
          initial={{ opacity: 0, y: -14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl" style={{
            background: 'linear-gradient(90deg, #1e40af, #0369a1)',
            border: '1px solid rgba(147,210,255,0.35)',
            boxShadow: '0 6px 24px rgba(14,165,233,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
          }}>
            <div className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0" style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <Globe className="w-3.5 h-3.5 text-sky-200" />
            </div>
            <span className="font-black uppercase whitespace-nowrap" style={{ fontSize: '0.8rem', letterSpacing: '0.07em', color: '#fff' }}>
              {isEn ? 'International Students' : 'Uluslararası Öğrenciler'}
            </span>
          </div>
        </motion.div>

        {/* Sol içerik — max %44 genişlik */}
        <motion.div
          className="absolute bottom-0 left-0 p-9"
          style={{ maxWidth: '44%' }}
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Kampüs etiketi */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.18em] mb-3"
            style={{
              background: 'rgba(123,13,30,0.55)',
              border: '1px solid rgba(210,60,80,0.3)',
              color: '#fca5a5',
              backdropFilter: 'blur(8px)',
            }}>
            <MapPin className="w-2.5 h-2.5" />
            {isEn ? 'Beytepe Campus · Ankara' : 'Beytepe Kampüsü · Ankara'}
          </span>

          {/* Başlık */}
          <h2 className="font-black text-white leading-[1.08] mb-2.5"
            style={{ fontSize: 'clamp(1.55rem, 2.6vw, 2.3rem)' }}>
            {isEn
              ? <>Shape Your Future<br /><span style={{ color: '#fca5a5' }}>at Faculty of Science</span></>
              : <>Bilimi Keşfet,<br /><span style={{ color: '#fca5a5' }}>Geleceğini Şekillendir</span></>
            }
          </h2>

          {/* Açıklama — 2 satırla sınırlı */}
          <p className="text-white/55 text-[13px] leading-relaxed mb-3"
            style={{ maxWidth: '34ch', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {isEn
              ? 'Hacettepe University Faculty of Science offers undergraduate and graduate programmes across 5 disciplines with world-class researchers and state-of-the-art labs.'
              : 'Aktüerya, Biyoloji, Kimya, Matematik ve İstatistik bölümlerinde öncü akademisyenler ve modern araştırma laboratuvarlarıyla geleceğini şekillendir.'
            }
          </p>

          {/* İstatistik satırı */}
          <div className="flex gap-4 mb-3">
            {[
              { val: '2.627+', label: isEn ? 'Undergrad Students' : 'Lisans Öğrencisi' },
              { val: '183',    label: isEn ? 'Faculty Members'    : 'Öğretim Üyesi'   },
              { val: '103+',   label: isEn ? 'Active Projects'    : 'Aktif Proje'      },
            ].map((s, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-white font-black text-xl leading-none tabular-nums">{s.val}</span>
                <span className="text-white/35 text-[9px] mt-0.5 uppercase tracking-wider">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="h-px mb-3" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.1), transparent)' }} />

          {/* Bölüm pilleri */}
          <div className="flex flex-wrap gap-1 mb-3">
            {departments.map(d => (
              <a key={d.name} href={d.href} target="_blank" rel="noopener noreferrer"
                className="text-[9px] font-semibold px-2.5 py-1 rounded-full transition-all duration-200 hover:bg-white/15 hover:text-white"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.13)',
                  color: 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(6px)',
                }}>
                {d.name}
              </a>
            ))}
          </div>

          {/* ── Hızlı Bağlantılar ── */}
          <div className="mb-4">
            <p className="text-white/60 text-[9px] font-black uppercase tracking-[0.24em] mb-1.5">
              {isEn ? 'Quick Links' : 'Hızlı Bağlantılar'}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { icon: GraduationCap, label: isEn ? 'Undergrad Programs'  : 'Lisans Programları',    href: `${prefix}/programlar/lisans`                },
                { icon: GraduationCap, label: isEn ? 'Graduate Programs'   : 'Lisansüstü Programlar', href: `${prefix}/programlar/yuksek-lisans`          },
                { icon: FlaskConical,  label: isEn ? 'Research Labs'       : 'Araştırma Lab.',        href: `${prefix}/arastirma/laboratuvarlar`          },
                { icon: Users,         label: isEn ? 'Academic Staff'      : 'Öğretim Üyeleri',      href: `${prefix}/fakulte-personeli/ogretim-uyeleri` },
                { icon: Plane,         label: isEn ? 'Erasmus'             : 'Erasmus Programı',     href: `${prefix}/programlar/erasmus`                },
                { icon: BookOpen,      label: isEn ? 'Student Clubs'       : 'Öğrenci Toplulukları', href: `${prefix}/ogrenci-topluluklari`              },
              ].map((ql, i) => {
                const Icon = ql.icon;
                return (
                  <Link key={i} to={ql.href}
                    className="group flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-white/10"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
                  >
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(123,13,30,0.4)', border: '1px solid rgba(200,60,80,0.22)' }}>
                      <Icon className="w-3 h-3" style={{ color: '#fca5a5' }} />
                    </div>
                    <span className="text-white/60 text-[10px] font-semibold group-hover:text-white/95 transition-colors leading-tight truncate flex-1">
                      {ql.label}
                    </span>
                    <ChevronRight className="w-3 h-3 text-white/18 group-hover:text-white/55 transition-colors flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          </div>

        </motion.div>

        {/* Sağ içerik — sağdan hizalı, max %42 genişlik */}
        <motion.div
          className="absolute bottom-0 right-0 p-10"
          style={{ maxWidth: '42%' }}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Parlak mavi badge */}
          <div className="inline-flex mb-5">
            <div className="relative">
              <div className="absolute -inset-1 rounded-2xl blur-md opacity-65" style={{
                background: 'linear-gradient(90deg, #1d4ed8, #0ea5e9, #06b6d4)'
              }} />
              <div className="relative flex items-center gap-3 px-4 py-2.5 rounded-2xl" style={{
                background: 'linear-gradient(90deg, #1e40af, #0369a1)',
                border: '1px solid rgba(147,210,255,0.35)',
                boxShadow: '0 6px 28px rgba(14,165,233,0.4), inset 0 1px 0 rgba(255,255,255,0.15)',
              }}>
                <div className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0" style={{
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
                }}>
                  <Globe className="w-3.5 h-3.5 text-sky-200" />
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-sky-200 text-[9px] font-black uppercase tracking-[0.26em] mb-0.5">
                    {isEn ? 'Admissions Open' : 'Başvurular Açık'}
                  </span>
                  <span className="text-white text-[12px] font-black uppercase tracking-[0.1em]">
                    {isEn ? 'International · 2026–2027' : 'Uluslararası Öğrenciler · 2026–2027'}
                  </span>
                </div>
                <span className="flex h-2.5 w-2.5 flex-shrink-0 ml-1">
                  <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 opacity-55" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                </span>
              </div>
            </div>
          </div>

          <h2 className="font-black text-white leading-[1.1] mb-3"
            style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.2rem)' }}>
            {isEn
              ? <>Welcome to<br /><span className="text-sky-300">Hacettepe University</span><br />Faculty of Science</>
              : <>Hacettepe Üniversitesi<br /><span className="text-sky-300">Fen Fakültesi</span>'ne<br />Hoş Geldiniz</>
            }
          </h2>

          <p className="text-white/50 text-[13px] leading-relaxed mb-6 max-w-xs">
            {isEn
              ? "Join Turkey's most prestigious science faculty alongside researchers from across the world."
              : 'Dünyanın dört bir yanından akademisyenlerle Türkiye\'nin köklü fen fakültesinde bilimi keşfet.'
            }
          </p>

          {/* Butonlar — premium */}
          <div className="flex flex-col gap-3">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <Link
                to={isEn ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler'}
                className="flex items-center justify-between gap-3 w-full px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  color: '#fff',
                  backdropFilter: 'blur(12px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
                }}
              >
                <span className="flex items-center gap-2.5">
                  <Globe className="w-4 h-4 text-sky-300" />
                  {isEn ? 'Detailed Information' : 'Detaylı Bilgi Al'}
                </span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Link>
            </motion.div>
            <motion.a
              href="https://bilsis.hacettepe.edu.tr/oibs/foa_app/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 w-full px-6 py-3.5 rounded-2xl font-bold text-sm text-white"
              style={{
                background: 'linear-gradient(90deg, #0369a1 0%, #0ea5e9 50%, #06b6d4 100%)',
                backgroundSize: '200% 100%',
                boxShadow: '0 6px 28px rgba(14,165,233,0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
              whileHover={{ backgroundPosition: 'right center', scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <span className="flex items-center gap-2.5">
                <MousePointer className="w-4 h-4" />
                {isEn ? 'Apply Now' : 'Hemen Başvur'}
              </span>
              <ArrowUpRight className="w-4 h-4" />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* ══════════ MOBİL — İki ayrı tam kart ══════════ */}
      <div className="md:hidden flex flex-col">

        {/* ── KART 1: ADAY ÖĞRENCİLER ── */}
        <div className="relative" style={{ minHeight: '500px' }}>
          <img src={PROSP_IMG1} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(165deg, rgba(50,3,12,0.82) 0%, rgba(88,7,19,0.75) 40%, rgba(8,1,4,0.96) 100%)'
          }} />
          <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(180,30,55,0.28) 0%, transparent 65%)'
          }} />

          <div className="relative z-10 flex flex-col h-full p-6" style={{ minHeight: '500px' }}>

            {/* Üst başlık */}
            <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl mb-3 self-start" style={{
              background: 'rgba(123,13,30,0.55)',
              border: '1px solid rgba(210,60,80,0.35)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 18px rgba(80,8,18,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
            }}>
              <div className="flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0" style={{
                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
              }}>
                <GraduationCap className="w-3 h-3" style={{ color: '#fca5a5' }} />
              </div>
              <span className="font-black uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: '#fff' }}>
                {isEn ? 'Prospective Students' : 'Aday Öğrenciler'}
              </span>
            </div>

            {/* Üst satır: badge + mini foto */}
            <div className="flex items-start justify-between mb-auto">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.18em]"
                style={{
                  background: 'rgba(123,13,30,0.6)',
                  border: '1px solid rgba(210,60,80,0.35)',
                  color: '#fca5a5',
                  backdropFilter: 'blur(10px)',
                }}>
                <MapPin className="w-2.5 h-2.5" />
                {isEn ? 'Beytepe Campus · Ankara' : 'Beytepe Kampüsü · Ankara'}
              </span>
              {/* hakkinda14 mini polaroid */}
              <motion.div
                initial={{ opacity: 0, rotate: 4, y: -8 }}
                whileInView={{ opacity: 1, rotate: 4, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{ transform: 'rotate(4deg)' }}
              >
                <div className="w-14 h-18 rounded-xl overflow-hidden" style={{
                  width: '3.5rem', height: '4.5rem',
                  border: '2px solid rgba(255,255,255,0.22)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                }}>
                  <img src={PROSP_IMG2} alt="" className="w-full h-full object-cover" />
                </div>
              </motion.div>
            </div>

            {/* Alt içerik */}
            <div className="mt-auto">
              {/* İstatistikler */}
              <div className="flex gap-5 mb-4">
                {[
                  { val: '2.627+', label: isEn ? 'Undergrads'   : 'Lisans Öğrencisi' },
                  { val: '183',    label: isEn ? 'Faculty'       : 'Öğretim Üyesi'   },
                  { val: '5',      label: isEn ? 'Departments'   : 'Bölüm'           },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col">
                    <span className="text-white font-black text-[1.35rem] leading-none tabular-nums">{s.val}</span>
                    <span className="text-white/40 text-[9px] mt-0.5 uppercase tracking-wider">{s.label}</span>
                  </div>
                ))}
              </div>

              <div className="h-px mb-4" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.12), transparent)' }} />

              {/* Başlık */}
              <h2 className="font-black text-white text-[1.6rem] leading-[1.15] mb-3">
                {isEn
                  ? <>Discover Your Future<br /><span style={{ color: '#fca5a5' }}>at Faculty of Science</span></>
                  : <>Geleceğini Şekillendir<br /><span style={{ color: '#fca5a5' }}>Fen Fakültesi'nde</span></>
                }
              </h2>

              {/* Bölüm pilleri — yatay kaydırmalı */}
              <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {departments.map(d => (
                  <a key={d.name} href={d.href} target="_blank" rel="noopener noreferrer"
                    className="flex-shrink-0 text-[9px] font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      color: 'rgba(255,255,255,0.65)',
                    }}>
                    {d.name}
                  </a>
                ))}
              </div>

              {/* Hızlı bağlantılar — 3 sütun ikon+etiket */}
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { icon: GraduationCap, label: isEn ? 'Undergrad'    : 'Lisans',        href: `${prefix}/programlar/lisans`                        },
                  { icon: GraduationCap, label: isEn ? 'Graduate'     : 'Lisansüstü',    href: `${prefix}/programlar/yuksek-lisans`                 },
                  { icon: FlaskConical,  label: isEn ? 'Research'     : 'Araştırma',     href: `${prefix}/arastirma/laboratuvarlar`                 },
                  { icon: Users,         label: isEn ? 'Staff'        : 'Akademisyen',   href: `${prefix}/fakulte-personeli/ogretim-uyeleri`        },
                  { icon: Plane,         label: 'Erasmus',                                href: `${prefix}/programlar/erasmus`                       },
                  { icon: BookOpen,      label: isEn ? 'Clubs'        : 'Topluluklar',   href: `${prefix}/ogrenci-topluluklari`                     },
                ].map((ql, i) => {
                  const Icon = ql.icon;
                  return (
                    <Link key={i} to={ql.href}
                      className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-all active:scale-95"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(123,13,30,0.45)', border: '1px solid rgba(200,60,80,0.25)' }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: '#fca5a5' }} />
                      </div>
                      <span className="text-white/65 text-[9px] font-bold leading-tight text-center">{ql.label}</span>
                    </Link>
                  );
                })}
              </div>

              {/* CTA — tam genişlik, premium */}
              <Link
                to={prefix + '/hakkimizda'}
                className="flex items-center justify-between gap-3 w-full px-6 py-4 rounded-2xl font-bold text-base text-white"
                style={{
                  background: 'linear-gradient(90deg, #7B0D1E, #b01228)',
                  boxShadow: '0 6px 24px rgba(123,13,30,0.6), inset 0 1px 0 rgba(255,255,255,0.15)',
                }}
              >
                <span>{isEn ? 'Explore the Faculty' : 'Fakülteyi Keşfet'}</span>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* ── KART 2: ULUSLARARASI ÖĞRENCİLER ── */}
        <div className="relative" style={{ minHeight: '400px' }}>
          <img src={INTL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(165deg, rgba(6,10,55,0.85) 0%, rgba(12,35,120,0.78) 45%, rgba(3,5,32,0.96) 100%)'
          }} />
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(56,189,248,0.22) 0%, transparent 65%)'
          }} />

          <div className="relative z-10 flex flex-col h-full p-6" style={{ minHeight: '400px' }}>

            {/* Üst başlık */}
            <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl mb-3 self-start" style={{
              background: 'linear-gradient(90deg, #1e40af, #0369a1)',
              border: '1px solid rgba(147,210,255,0.35)',
              boxShadow: '0 4px 18px rgba(14,165,233,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
            }}>
              <div className="flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0" style={{
                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
              }}>
                <Globe className="w-3 h-3 text-sky-200" />
              </div>
              <span className="font-black uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: '#fff' }}>
                {isEn ? 'International Students' : 'Uluslararası Öğrenciler'}
              </span>
            </div>

            {/* Üst: parlak badge */}
            <div className="mb-auto">
              <div className="inline-flex">
                <div className="relative">
                  <div className="absolute -inset-1 rounded-2xl blur-md opacity-60" style={{
                    background: 'linear-gradient(90deg, #1d4ed8, #0ea5e9, #06b6d4)'
                  }} />
                  <div className="relative flex items-center gap-2.5 px-4 py-2.5 rounded-2xl" style={{
                    background: 'linear-gradient(90deg, #1e40af, #0369a1)',
                    border: '1px solid rgba(147,210,255,0.35)',
                    boxShadow: '0 6px 24px rgba(14,165,233,0.4), inset 0 1px 0 rgba(255,255,255,0.15)',
                  }}>
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)' }}>
                      <Globe className="w-3 h-3 text-sky-200" />
                    </div>
                    <div className="flex flex-col leading-none">
                      <span className="text-sky-200 text-[8px] font-black uppercase tracking-[0.25em] mb-0.5">
                        {isEn ? 'Admissions Open' : 'Başvurular Açık'}
                      </span>
                      <span className="text-white text-[11px] font-black uppercase tracking-[0.08em]">
                        {isEn ? 'International Students · 2026–2027' : 'Uluslararası Öğrenciler · 2026–2027'}
                      </span>
                    </div>
                    <span className="flex h-2.5 w-2.5 flex-shrink-0 ml-1 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-55" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alt içerik */}
            <div className="mt-auto">
              <h2 className="font-black text-white text-[1.6rem] leading-[1.15] mb-2">
                {isEn
                  ? <>Welcome to<br /><span className="text-sky-300">Hacettepe University</span><br />Faculty of Science</>
                  : <>Hacettepe Üniversitesi<br /><span className="text-sky-300">Fen Fakültesi</span>'ne<br />Hoş Geldiniz</>
                }
              </h2>
              <p className="text-white/45 text-sm leading-relaxed mb-5">
                {isEn
                  ? "Join Turkey's most prestigious science faculty."
                  : 'Türkiye\'nin en köklü fen fakültesinde bilimi keşfet.'
                }
              </p>
              <div className="flex flex-col gap-2.5">
                <Link
                  to={isEn ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler'}
                  className="flex items-center justify-between gap-3 w-full px-5 py-3.5 rounded-2xl font-bold text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.09)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    color: '#fff',
                    backdropFilter: 'blur(12px)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
                  }}
                >
                  <span className="flex items-center gap-2.5">
                    <Globe className="w-4 h-4 text-sky-300" />
                    {isEn ? 'Detailed Information' : 'Detaylı Bilgi Al'}
                  </span>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </Link>
                <a
                  href="https://bilsis.hacettepe.edu.tr/oibs/foa_app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 w-full px-5 py-3.5 rounded-2xl font-bold text-sm text-white"
                  style={{
                    background: 'linear-gradient(90deg, #0369a1, #0ea5e9)',
                    boxShadow: '0 6px 24px rgba(14,165,233,0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
                  }}
                >
                  <span className="flex items-center gap-2.5">
                    <MousePointer className="w-4 h-4" />
                    {isEn ? 'Apply Now' : 'Hemen Başvur'}
                  </span>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>

    </motion.div>
  );
};


/* ── Kart Bileşeni ─────────────────────────────────────────────────────────── */
const Card = ({ title, tag, desc, image, href, external, subLinks = [], accent = '#fff', exploreLabel = 'Keşfet' }) => {
  const isExternal = external || (href && href.startsWith('http'));

  const inner = (
    <div className="relative group h-full overflow-hidden rounded-3xl cursor-pointer">
      <img
        src={image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.06]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" style={{ height: '55%', top: 'auto' }} />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-7">
        <span
          className="text-[9px] font-black uppercase tracking-[0.25em] mb-3 block opacity-70"
          style={{ color: accent, textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}
        >
          {tag}
        </span>

        <h3
          className="text-xl md:text-2xl font-black text-white leading-tight transition-transform duration-400 group-hover:-translate-y-2"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.95)' }}
        >
          {title}
        </h3>

        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 overflow-hidden">
          <div className="overflow-hidden">
            {desc && (
              <p className="text-sm text-white/65 mt-3 leading-relaxed line-clamp-2">{desc}</p>
            )}

            {subLinks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {subLinks.slice(0, 5).map((l, i) => (
                  l.href.startsWith('http') ? (
                    <a key={i} href={l.href} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </a>
                  ) : (
                    <Link key={i} to={l.href} onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </Link>
                  )
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5 mt-4 text-white text-xs font-bold tracking-wide">
              <span>{exploreLabel}</span>
              <ArrowUpRight size={13} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!href) return inner;
  if (isExternal)
    return <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>;
  return <Link to={href} className="block h-full">{inner}</Link>;
};

/* ── Ana Bileşen ───────────────────────────────────────────────────────────── */
const ShowcaseGrid = () => {
  const { t, i18n } = useTranslation();
  const prefix = i18n.language === 'en' ? '/en' : '';

  const isEn = i18n.language === 'en';

  const cards = [
    {
      tag: isEn ? 'Academic' : 'Akademik',
      title: t('showcase.departments'),
      desc: isEn
        ? 'Five world-class departments: Actuarial Science, Biology, Chemistry, Mathematics and Statistics.'
        : 'Aktüerya, Biyoloji, Kimya, Matematik ve İstatistik bölümleriyle bilimin öncüsü.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/MATEMAT%C4%B0K.jpg',
      subLinks: [
        { text: isEn ? 'Actuarial Sci.' : 'Aktüerya',   href: 'https://aktuerya.hacettepe.edu.tr/' },
        { text: isEn ? 'Biology'        : 'Biyoloji',   href: 'https://biology.hacettepe.edu.tr/' },
        { text: isEn ? 'Chemistry'      : 'Kimya',      href: 'https://chem.hacettepe.edu.tr/' },
        { text: isEn ? 'Mathematics'    : 'Matematik',  href: 'https://mat.hacettepe.edu.tr/' },
        { text: isEn ? 'Statistics'     : 'İstatistik', href: 'https://stat.hacettepe.edu.tr/' },
      ],
    },
    {
      tag: isEn ? 'Education' : 'Eğitim',
      title: t('showcase.academicPrograms'),
      desc: isEn
        ? 'Undergraduate, graduate and doctoral programmes for an academic career.'
        : 'Lisans, yüksek lisans ve doktora programlarıyla akademik kariyer.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf.jpg',
      href: prefix + '/programlar/lisans',
      subLinks: [
        { text: isEn ? 'Undergraduate' : 'Lisans',        href: prefix + '/programlar/lisans' },
        { text: isEn ? 'Master\'s'     : 'Yüksek Lisans', href: prefix + '/programlar/yuksek-lisans' },
        { text: isEn ? 'Doctorate'     : 'Doktora',       href: prefix + '/programlar/doktora' },
        { text: 'Erasmus',                                 href: prefix + '/programlar/erasmus' },
      ],
    },
    {
      tag: isEn ? 'Research' : 'Araştırma',
      title: t('showcase.researchLabs'),
      desc: t('showcase.labsDesc'),
      image: 'https://lab.hacettepe.edu.tr/fs_/labs/hucrefizyolojisi1.jpg',
      href: prefix + '/arastirma/laboratuvarlar',
    },
    {
      tag: isEn ? 'Faculty' : 'Kadro',
      title: t('showcase.faculty'),
      desc: t('showcase.facultyDesc'),
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf1.jpg',
      href: prefix + '/fakulte-personeli/ogretim-uyeleri',
    },
    {
      tag: isEn ? 'Publication' : 'Yayın',
      title: t('showcase.journals'),
      desc: t('showcase.journalsDesc'),
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80',
      href: prefix + '/arastirma/dergiler',
    },
    {
      tag: isEn ? 'Research & Innovation' : 'Araştırma & İnovasyon',
      title: t('showcase.ongoingProjects'),
      desc: t('showcase.projectsDesc'),
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=1600&q=80',
      href: prefix + '/arastirma/devam-eden-projelerimiz',
    },
  ];

  return (
    <section className="bg-white py-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Çapraz İkili Banner: Aday Öğrenciler + Uluslararası ── */}
        <DiagonalDualBanner isEn={isEn} prefix={prefix} />

        {/* ── Başlık ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#7B0D1E] mb-2.5">
              {isEn ? 'Hacettepe University · Faculty of Science' : 'Hacettepe Üniversitesi · Fen Fakültesi'}
            </p>
            <h2 className="text-3xl md:text-[2.25rem] font-black text-gray-900 leading-tight">
              {t('showcase.title')}
            </h2>
          </div>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed md:text-right md:pb-1">
            {t('showcase.subtitle')}
          </p>
        </motion.div>

        {/* ── 3×2 Eşit Grid ── */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {cards.map((card, i) => (
            <div key={i} style={{ height: '280px' }}>
              <Card {...card} exploreLabel={isEn ? 'Explore' : 'Keşfet'} />
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ShowcaseGrid;
